/**
 * What the public status page probes, and what each answer means.
 *
 * Pure and React-free on purpose: `scripts/status-check.mjs` imports this
 * module to probe the same paths for real and fail if any service stops
 * answering what the page calls healthy. Same arrangement as
 * `lib/board-select.ts` — the rules that can silently become wrong live where
 * something can check them.
 */

export type State = 'up' | 'down' | 'absent' | 'checking'

export interface Check {
  id: string
  label: string
  /** One line, in the language of what breaks for a person when it is down. */
  detail: string
  path: string
  /** HTTP codes that mean "this service is answering". */
  ok: number[]
  /**
   * Text the response body must contain. Caddy serves the SPA's index.html for
   * any path it cannot find, so a 200 alone proves nothing about a static
   * asset — the same trap scripts/check.sh documents.
   */
  needle?: string
  /**
   * When the body check fails, is that an OUTAGE or just "not installed here"?
   * The decks are deliberately left out of the deployed bundle (see README), so
   * their absence in production is a fact about the deployment, not a fault.
   */
  absentWhenMissing?: boolean
}

const CHECKS: Check[] = [
  {
    id: 'portal',
    label: 'Portal',
    detail: 'The web app itself — pages, assets, fonts.',
    path: '/',
    ok: [200],
  },
  {
    id: 'config',
    label: 'Configuration',
    detail: 'The keys and URLs the app reads at boot. Without it nothing else starts.',
    path: '/config.json',
    ok: [200],
    needle: 'anonKey',
  },
  {
    id: 'auth',
    label: 'Sign-in',
    detail: 'Magic links and sessions. A failure here means nobody new can get in.',
    path: '/auth/v1/health',
    ok: [200],
  },
  {
    id: 'api',
    label: 'Data API',
    detail: 'Every read and write the app makes. The one to watch.',
    path: '/rest/v1/',
    ok: [200, 401],
  },
  {
    id: 'storage',
    label: 'File storage',
    detail: 'Uploaded files and images.',
    path: '/storage/v1/status',
    ok: [200],
  },
  {
    id: 'realtime',
    label: 'Live updates',
    detail: 'Boards and discussion updating without a refresh. 403 here is healthy — it is refusing an anonymous request.',
    path: `/realtime/v1/api/tenants/${tenantSlug()}`,
    ok: [200, 401, 403],
  },
  {
    id: 'functions',
    label: 'Edge functions',
    detail: 'Token exchange for the CLI and MCP server. 405 is healthy — it wants a POST.',
    path: '/functions/v1/mcp-auth',
    ok: [200, 204, 400, 401, 404, 405],
  },
  {
    id: 'decks',
    label: 'Presentations',
    detail: 'The built Slidev decks. Not deployed everywhere — absent is not an outage.',
    path: '/presentations/git-overview/index.html',
    ok: [200],
    needle: '/presentations/git-overview/assets',
    absentWhenMissing: true,
  },
]

/**
 * The tenant slug, taken from the hostname's first label.
 *
 * Realtime resolves a tenant the same way (see the README's realtime notes), so
 * this is the same string it will look for. `localhost` has no label to take,
 * and the local tenant is `teamaccel`, which keeps the check meaningful in dev
 * instead of probing a tenant that cannot exist.
 */
function tenantSlug(): string {
  if (typeof window === 'undefined') return 'teamaccel'
  const host = window.location.hostname
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return 'teamaccel'
  return host.split('.')[0] || 'teamaccel'
}

export interface Result {
  state: State
  status?: number
  ms?: number
  note?: string
}

/** What a finished probe means. Pure, so the check script can assert it. */
export function classify(check: Check, status: number | null, body: string | null): Result {
  if (status === null) return { state: 'down', note: 'No response' }
  if (!check.ok.includes(status)) return { state: 'down', status, note: `HTTP ${status}` }
  if (check.needle && !(body ?? '').includes(check.needle)) {
    return check.absentWhenMissing
      ? { state: 'absent', status, note: 'Not deployed here' }
      : { state: 'down', status, note: 'Answered, but with the wrong thing' }
  }
  return { state: 'up', status }
}


/** The probe table, for the page and for scripts/status-check.mjs. */
export const STATUS_CHECKS = CHECKS
